import React, { useReducer, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';  // NEW

// --- Helpers ---
const PAID_PRICE_TIERS = {
  'Starter Membership': 'Starter',
  'Professional Membership': 'Professional',
  'Business Membership': 'Business'
};
const CREDIT_PACK_SIZES = [25, 50, 100, 250, 500, 1000];
const urlParams = new URLSearchParams(window.location.search);


// --- State Reducer ---
const initialState = { loading: false, error: null, success: null };
const stateReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING': return { ...state, loading: action.payload, error: null, success: null };
    case 'SET_ERROR': return { ...state, loading: false, error: action.payload, success: null };
    case 'SET_SUCCESS': return { ...state, loading: false, error: null, success: action.payload };
    default: return state;
  }
};

// --- Component Definition ---
function NewUserSubscriptionView({ subscriptionProducts, creditPackProducts, showFreeTrial }) {
  // --- ADDED LOG to check incoming props ---
  console.log("[NewUserSubscriptionView] Rendering. Props received:", {
      showFreeTrial,
      subscriptionProductsCount: subscriptionProducts?.length ?? 0,
      creditPackProductsCount: creditPackProducts?.length ?? 0 // Check if credit packs are arriving
  });
  // --- END LOG ---

  const { user, supabase, fetchWrapper } = useAuth();
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const navigate = useNavigate();  // Hook

  // In NewUserSubscriptionView.jsx (replace stub)
const handleFreeMembership = async () => {
  
  
  dispatch({ type: 'SET_LOADING', payload: true });
  try {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 30);

    const insertResponse = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/insert_membership2`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        p_user_id: user.id,
        p_tier: 'Trial Membership',
        p_status: 'active',
        p_start_date: new Date().toISOString(),
        p_end_date: endDate.toISOString(),
        p_image_credits: 25,
        p_amount: 0.00
      }),
    });

    const insertData = await insertResponse.json();
    if (!insertResponse.ok || insertData.error) {
      throw new Error(insertData.error || 'Insert failed');
    }

    // Refresh (AuthContext re-runs check_membership2)
    window.dispatchEvent(new Event('membershipUpdated'));
    dispatch({ type: 'SET_SUCCESS', payload: 'Free trial activated! 25 Upload Credits for 30 days.' });
    const updatedType = urlParams.get('updated_type') || 'free';
    setTimeout(() => {
      navigate('/estate-sales-pipeline/active-member-view?success=true&updated=true&updated_type=free', { replace: true });  // Dynamic nav
    }, 2000);  // Toast read time
  } catch (err) {
    console.error('Free membership error:', err);
    dispatch({ type: 'SET_ERROR', payload: `Trial failed: ${err.message}` });
  } finally {
    dispatch({ type: 'SET_LOADING', payload: false });
  }
};
 // src/components/NewUserSubscriptionView.jsx

const handleStripePurchase = async (priceId) => {
  dispatch({ type: 'SET_LOADING', payload: true });
  try {
    // --- THIS IS THE FIX ---
    // Changed 'fetch' to 'fetchWrapper'
    const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/stripe/create-checkout-session`, {
    // --- END FIX ---
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        clientId: user.id,
        priceId: priceId,
        mode: 'subscription'
      }),
      // 'credentials: include' is likely handled by fetchWrapper,
      // but you can leave it if your wrapper merges options.
      credentials: 'include', 
    });
    
    const session = await response.json();
    if (!response.ok || session.error) {
      throw new Error(session.error || `Failed to create checkout session (Status: ${response.status})`);
    }
    
    window.location.href = session.url;
  } catch (err) {
    console.error('Stripe purchase error:', err);
    dispatch({ type: 'SET_ERROR', payload: `Stripe error: ${err.message}` });
  } finally {
    dispatch({ type: 'SET_LOADING', payload: false });
    console.log('Attempting purchase with priceId:', priceId);
  }
};
  // --- useMemo for Credit Pack Table Data ---
  const creditPackTable = useMemo(() => {
    // Check if products are available
    if (!creditPackProducts || creditPackProducts.length === 0) {
        console.log("[NewUserSubscriptionView] creditPackProducts empty or null in useMemo.");
        return []; // Return empty array if no data
    }
    // console.log("[NewUserSubscriptionView] Building creditPackTable...");

    return CREDIT_PACK_SIZES.map(size => {
      const row = { sizeLabel: `${size.toLocaleString()} Upload Credits`, sizeNumeric: size, prices: {} };
      for (const [tierKey, tierShortName] of Object.entries(PAID_PRICE_TIERS)) {
        const product = creditPackProducts.find(p =>
          p?.tier?.includes(tierShortName) && p?.image_credits === size && !p?.is_membership
        );
        row.prices[tierKey] = product ? { costPerImage: Number(product.cost_per_image) || 0 } : null;
        
      }
      return row;
    });
  }, [creditPackProducts]); // Dependency: recalculate if products change


  // --- Render Functions ---

  // Renders subscription boxes
  const renderProduct = (product) => {
    const price = typeof product.cost_per_image === 'number' ? product.cost_per_image : 0;
    const isFree = price === 0 || product.tier === 'TRIAL MEMBERSHIP';
    if (isFree && !showFreeTrial) return null;

    return (
      <div key={product.id} className="flex-1 min-w-[250px] m-4 p-6 border border-gray-300 rounded-lg shadow-lg bg-white flex flex-col">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-3">{product.tier.replace(' MEMBERSHIP', '').toUpperCase()}</h3>
        <p className="text-gray-600 mb-4 h-24 overflow-y-auto">{product.description || 'N/A'}</p>
        <div className="mb-4 text-center">
          <span className="text-4xl font-bold">{isFree ? 'FREE' : `$${price.toFixed(2)}`}</span>
          <span className="text-lg text-gray-500">{isFree ? ' for 30 days' : ' /mo'}</span>
        </div>
        {isFree ? (
          <p className="text-sm text-gray-600 mb-4 text-center">
            No Credit Card Required. Enjoy 25 Upload Credits ($10 Value) during your 30-day trial.
          </p>
        ) : (
          <p className="text-sm text-gray-600 mb-4 text-center">
            Billed monthly. Renews automatically until you cancel.
          </p>
        )}
        <button
          onClick={() => isFree ? handleFreeMembership() : handleStripePurchase(product.stripe_price_id)}
          disabled={state.loading}
          // *** FIX: Changed Free Trial button to blue ***
          className={`mt-auto w-full py-3 px-5 rounded-md text-white font-semibold transition-colors disabled:bg-gray-400 bg-blue-600 hover:bg-blue-700`}
          // Old green style: ${isFree ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
        >
          {state.loading ? 'Processing...' : (isFree ? 'Start Free Trial' : 'Subscribe')}
        </button>
      </div>
    );
  };

  // Renders the informational credit pack table
  const renderCreditPackTable = () => (
    <div className="bg-white p-6 rounded-lg shadow-lg mt-12">
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
        Detailed Upload Credit Pricing Comparison
      </h2>
      <p className="text-sm text-gray-600 mb-4 text-center">
        Not sure how many credits you need? For reference, 15-20 credits covers a single cluttered room, while 175-225 credits is perfect for a typical mid-sized estate sale.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="divide-x divide-gray-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Credit Pack Size
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trial
              </th>
              {Object.values(PAID_PRICE_TIERS).map(tierName => (
                <th
                  key={tierName}
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {tierName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Show loading state within the table body */}
            {creditPackTable.length === 0 ? (
              <tr>
                <td
                  colSpan={Object.keys(PAID_PRICE_TIERS).length + 2}
                  className="text-center py-4 text-gray-500"
                >
                  Loading credit prices...
                </td>
              </tr>
            ) : (
              creditPackTable.map(row => (
                <tr key={row.sizeLabel} className="divide-x divide-gray-200">
                  <td className="px-6 py-2 whitespace-nowrap font-semibold text-gray-900">
                    {row.sizeLabel}
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap text-center">
                    {row.sizeNumeric === 25 ? (
                      <span className="py-2 px-4 rounded-md font-semibold text-sm text-gray-500">
                        $10.00
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  {Object.keys(PAID_PRICE_TIERS).map(tierKey => {
                    const priceInfo = row.prices[tierKey];
                    const isValidPrice =
                      priceInfo && typeof priceInfo.costPerImage === 'number' && priceInfo.costPerImage >= 0;
                    const totalPrice = isValidPrice
                      ? (priceInfo.costPerImage * row.sizeNumeric).toFixed(2)
                      : 'N/A';
                    const showDash = tierKey === 'Business Membership' && row.sizeNumeric === 25;
                    return (
                      <td key={tierKey} className="px-6 py-2 whitespace-nowrap text-center">
                        {showDash ? (
                          <span className="text-gray-400 font-semibold">-</span>
                        ) : isValidPrice ? (
                          <span className="py-2 px-4 rounded-md font-semibold text-sm text-gray-500">
                            ${totalPrice}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
         <p className="text-center text-sm text-gray-500 mt-4">Choose a membership plan above to unlock the corresponding Upload Credit prices.</p>
      </div>
    </div>
  );

  // --- Main Component Render ---
  return (
    <div className="w-full max-w-7xl mx-auto p-8">
      <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-4">
        Choose Your Plan
      </h2>
      <p className="text-xl text-center text-gray-600 mb-6">
        {showFreeTrial
          ? "Claim your free 30-day trial (includes $10 in Upload Credits) or select a membership plan below."
          : "This page is only available for new accounts. If you’re seeing this by mistake, please contact support."
        }
      </p>
      <div className="mb-10">
        <h2 className="text-base font-semibold text-center mb-2">
          Included with Every Plan
        </h2>
        <ul className="text-sm md:text-base text-gray-800 mb-3 flex flex-col md:flex-row md:flex-wrap justify-center gap-3 text-center">
          <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
            <p>
              <span className="block font-semibold text-gray-900 mb-1">Unlimited Inventory Capacity</span>
              <span className="block text-xs md:text-sm text-gray-600">Scale your sales without restrictions. Add thousands of items per estate without ever hitting a limit or paywall.</span>
            </p>
          </li>
          <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
            <p>
              <span className="block font-semibold text-gray-900 mb-1">Smart Listing Assistant</span>
              <span className="block text-xs md:text-sm text-gray-600">Stop typing from scratch. Instantly generate professional titles, descriptions, and intelligent price estimates for every item.</span>
            </p>
          </li>
          <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
            <p>
              <span className="block font-semibold text-gray-900 mb-1">Master Price List</span>
              <span className="block text-xs md:text-sm text-gray-600">Download your complete inventory and empower staff to look up the correct price for missing or removed tags.</span>
            </p>
          </li>
          <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
            <p>
              <span className="block font-semibold text-gray-900 mb-1">Remote Sale Prep</span>
              <span className="block text-xs md:text-sm text-gray-600">Upload client photos and start pricing your sale from the comfort of your home office. Get the hard work done before you ever set foot on the property.</span>
            </p>
          </li>
          <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
            <p>
              <span className="block font-semibold text-gray-900 mb-1">Universal Tag Printing</span>
              <span className="block text-xs md:text-sm text-gray-600">Print how you want. Format tags for standard home laser and inkjet printers (PDF) or mobile thermal label makers (Brother/Dymo/Zebra/Rollo).</span>
            </p>
          </li>
          <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
            <p>
              <span className="block font-semibold text-gray-900 mb-1">100% Upload Credit Rollover</span>
              <span className="block text-xs md:text-sm text-gray-600">Unused upload credits automatically roll over to the next month so you can bank them for your busy season.</span>
            </p>
          </li>
        </ul>
      </div>

      {/* Error/Success Feedback */}
      {state.error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert"><strong>Error:</strong> {state.error}</div>}
      {state.success && (
  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6 flex items-center justify-center">
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700 mr-2"></div>
    {state.success}
  </div>
)}

      {/* Render the subscription boxes */}
      <div className="flex flex-wrap justify-center items-stretch mb-12">
        {/* Safety check for subscriptionProducts */}
        {!subscriptionProducts || subscriptionProducts.length === 0 ? (
            <p className="text-center text-gray-500">Loading membership plans...</p>
        ) : (
            subscriptionProducts
              .sort((a, b) => (a.cost_per_image ?? 0) - (b.cost_per_image ?? 0))
              .map(renderProduct)
        )}
      </div>

      {/* Render credit pack table */}
      {/* Safety check updated to look at the memoized table data */}
      {creditPackTable.length === 0 ? (
           <p className="text-center mt-8 text-gray-500">Loading credit price comparison...</p>
      ) : (
          renderCreditPackTable()
      )}
    </div>
  );
}

export default NewUserSubscriptionView;

